'use client';

import type { TextFieldProps } from '@mui/material/TextField';

import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import TextField from '@mui/material/TextField';

import {
  getSeparators,
  currencyDecimals,
  localeForCurrency,
  parseCurrencyInput,
  formatCurrencyValue,
} from 'src/utils/currency-format';

// ----------------------------------------------------------------------

export type CurrencyInputProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  /** Numeric value (null when empty). */
  value: number | null | undefined;
  /** Called with the parsed numeric value (null when empty). */
  onChange: (value: number | null) => void;
  /** ISO-4217 currency the org/user has configured (drives separators + decimals). */
  currency?: string;
  /** Optional explicit BCP-47 locale override (otherwise derived from currency). */
  locale?: string;
};

/**
 * Controlled text input that formats a monetary amount with thousands/decimal
 * separators matching the org/user country (via currency → locale). Stores a
 * raw `number` and renders the locale-grouped string.
 */
export function CurrencyInput({
  value,
  onChange,
  currency,
  locale: localeProp,
  ...other
}: CurrencyInputProps) {
  const locale = localeProp ?? localeForCurrency(currency);
  const maxDecimals = useMemo(() => currencyDecimals(currency), [currency]);
  const { decimal } = useMemo(() => getSeparators(locale), [locale]);

  const focusedRef = useRef(false);
  const [display, setDisplay] = useState<string>(() =>
    formatCurrencyValue(value, locale, maxDecimals)
  );

  // Keep the display in sync when the external value/locale changes while the
  // field is not being actively edited (e.g. currency switch, form reset).
  useEffect(() => {
    if (!focusedRef.current) {
      setDisplay(formatCurrencyValue(value, locale, maxDecimals));
    }
  }, [value, locale, maxDecimals]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = parseCurrencyInput(event.target.value, locale, maxDecimals);
      setDisplay(parsed.display);
      onChange(parsed.value);
    },
    [locale, maxDecimals, onChange]
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      focusedRef.current = true;
      other.onFocus?.(event);
    },
    [other]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      focusedRef.current = false;
      // Canonical re-format on blur.
      setDisplay(formatCurrencyValue(value, locale, maxDecimals));
      other.onBlur?.(event);
    },
    [value, locale, maxDecimals, other]
  );

  return (
    <TextField
      {...other}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      slotProps={{
        ...other.slotProps,
        htmlInput: {
          ...other.slotProps?.htmlInput,
          inputMode: maxDecimals > 0 ? 'decimal' : 'numeric',
          // Allow digits and the locale decimal separator only.
          pattern: maxDecimals > 0 ? `[0-9${decimal}]*` : '[0-9]*',
          autoComplete: 'off',
        },
      }}
    />
  );
}
